import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Dialog, LinearProgress, TextField } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import MovieRoundedIcon from "@mui/icons-material/MovieRounded";
import ReportProblemRoundedIcon from "@mui/icons-material/ReportProblemRounded";
import { Spinner } from "../../components/UI/States";
import { Notice, SearchField } from "../../components/Fleet/FleetParts";
import { TONE_CHIP, daysAgo, formatDateTime } from "../../components/Fleet/fleetFormat";
import { MAX_VIDEO_MB, VIDEO_KINDS, fetchSafetyConcern, searchPeople, uploadSafetyVideo } from "./safetyApi";

// Shared pieces of the Safety pages: chips, progress bar, list card, URL
// filters, a people picker, the concern detail dialog, the video upload
// dialog, and a confirm dialog.

// ---------------------------------------------------------------- display

// Statuses match the mobile app (safety-submissions.routes.js).
export const CONCERN_STATUS = {
  open: { label: "Open", tone: "rose" },
  in_review: { label: "In review", tone: "amber" },
  resolved: { label: "Resolved", tone: "emerald" },
};

export const Chip = ({ tone = "slate", children }) => (
  <span
    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
      TONE_CHIP[tone] || TONE_CHIP.slate
    }`}
  >
    {children}
  </span>
);

export const ConcernStatusChip = ({ status }) => {
  const meta = CONCERN_STATUS[status] || CONCERN_STATUS.open;
  return <Chip tone={meta.tone}>{meta.label}</Chip>;
};

export const percent = (part, whole) => (whole > 0 ? Math.round((part / whole) * 100) : 0);

export const plural = (n, word, many = `${word}s`) =>
  `${Number(n).toLocaleString()} ${n === 1 ? word : many}`;

export const overdueText = (dueAt) => {
  const days = daysAgo(dueAt);
  if (days === null || days <= 0) return "Due today";
  return `${plural(days, "day")} overdue`;
};

/**
 * Completed / overdue split of a set of assignments as one bar. Widths are
 * shares of `total`; what's left reads as "not done yet".
 */
export const ProgressBar = ({ completed, overdue = 0, total, label }) => (
  <div
    role="img"
    aria-label={label || `${completed} of ${total} complete${overdue ? `, ${overdue} overdue` : ""}`}
    className="flex h-2 w-full overflow-hidden rounded-full bg-[color:var(--app-hover)]"
  >
    <span className="h-full bg-emerald-500" style={{ width: `${percent(completed, total)}%` }} />
    <span className="h-full bg-rose-500" style={{ width: `${percent(overdue, total)}%` }} />
  </div>
);

/** Card with a heading row; children usually start with a divided list. */
export const ListCard = ({ title, description, actions, children, className = "" }) => (
  <div className={`app-card overflow-hidden rounded-2xl border ${className}`}>
    {(title || actions) && (
      <div className="flex flex-wrap items-start justify-between gap-3 px-4 pt-4 sm:px-5">
        <div className="min-w-0">
          {title && <h2 className="text-lg font-semibold tracking-tight">{title}</h2>}
          {description && <p className="app-muted text-sm">{description}</p>}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
    )}
    {children}
  </div>
);

export const AllClear = ({ children }) => (
  <p className="app-muted flex items-center gap-2 px-4 py-6 text-sm sm:px-5">
    <CheckCircleRoundedIcon className="text-emerald-600 dark:text-emerald-400" fontSize="small" />
    {children}
  </p>
);

// ---------------------------------------------------------------- filters

/**
 * Filters kept in the URL (?branch=&status=&q=) so links from the overview
 * land pre-filtered and a refresh keeps them. `status` falls back to
 * `defaultStatus` when missing or not one of `statuses`.
 */
export const useUrlFilters = (statuses = [], defaultStatus = "all") => {
  const [params, setParams] = useSearchParams();
  const raw = params.get("status");
  const set = (key, value) => {
    const next = new URLSearchParams(params);
    if (value && !(key === "status" && value === defaultStatus)) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };
  return {
    branch: params.get("branch") || "",
    status: statuses.includes(raw) ? raw : defaultStatus,
    query: params.get("q") || "",
    set,
  };
};

/** The branch option matching the URL value (links may differ in case). */
export const pickBranch = (branches, wanted) =>
  branches.find((b) => b.toLowerCase() === String(wanted || "").toLowerCase()) || "";

export const sameBranch = (a, b) => String(a || "").toLowerCase() === String(b || "").toLowerCase();

export const matchesQuery = (query, ...fields) => {
  const q = query.trim().toLowerCase();
  return !q || fields.some((f) => String(f || "").toLowerCase().includes(q));
};

// ---------------------------------------------------------------- people

/** One person in a picker list: name, then branch · email (or a reason). */
export const PersonOption = ({ person, detail, added, onAdd }) => (
  <button
    type="button"
    disabled={added}
    onClick={() => onAdd(person)}
    className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition hover:bg-[color:var(--app-hover)] disabled:opacity-50"
  >
    <span className="min-w-0">
      <span className="block truncate font-semibold">{person.name}</span>
      <span className="app-muted block truncate text-xs">
        {detail || [person.branch, person.email].filter(Boolean).join(" · ")}
      </span>
    </span>
    {added ? <span className="app-muted text-xs">Added</span> : <AddRoundedIcon fontSize="small" className="app-muted" />}
  </button>
);

/**
 * Picked people as removable chips, plus a debounced directory search
 * (2+ characters) to add more. people: [{ name, email, branch }].
 */
export const PeoplePicker = ({ userId, people, onAdd, onRemove, placeholder = "Search by name, email, or branch…" }) => {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (search.trim().length < 2) {
      setResults([]);
      setSearching(false);
      return undefined;
    }
    setSearching(true);
    let active = true;
    const timer = setTimeout(() => {
      searchPeople(userId, search.trim())
        .then((data) => active && setResults(data.people))
        .catch(() => active && setResults([]))
        .finally(() => active && setSearching(false));
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [search, userId]);

  return (
    <>
      {people.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {people.map((p) => (
            <span key={p.email} className="app-chip">
              {p.name}
              <button
                type="button"
                onClick={() => onRemove(p.email)}
                className="app-muted -mr-1 rounded-full hover:text-[color:var(--app-text)]"
                aria-label={`Remove ${p.name}`}
              >
                <CloseRoundedIcon sx={{ fontSize: 14 }} />
              </button>
            </span>
          ))}
        </div>
      )}
      <SearchField value={search} onChange={setSearch} placeholder={placeholder} className="w-full" />
      {searching && (
        <p className="app-muted mt-2 flex items-center gap-2 text-xs">
          <Spinner size={14} /> Searching…
        </p>
      )}
      {!searching && results.length > 0 && (
        <ul className="mt-2 max-h-56 divide-y divide-[color:var(--app-divider)] overflow-y-auto rounded-xl border app-divider">
          {results.map((p) => (
            <li key={p.email}>
              <PersonOption person={p} added={people.some((x) => x.email === p.email)} onAdd={onAdd} />
            </li>
          ))}
        </ul>
      )}
      {!searching && search.trim().length >= 2 && results.length === 0 && (
        <p className="app-muted mt-2 text-xs">Nobody matches “{search.trim()}”.</p>
      )}
    </>
  );
};

// ---------------------------------------------------------------- dialogs

export const DialogHeader = ({ id, icon: Icon, tone = "rose", title, subtitle, onClose, closeDisabled }) => (
  <div className="flex items-start gap-3">
    <span className={`app-icon-tile h-10 w-10 rounded-xl ${TONE_CHIP[tone]}`}>
      <Icon fontSize="small" />
    </span>
    <div className="min-w-0 flex-1">
      <h2 id={id} className="text-lg font-semibold leading-tight">
        {title}
      </h2>
      {subtitle && <p className="app-muted text-sm">{subtitle}</p>}
    </div>
    <button
      type="button"
      className="app-icon-btn -mr-2 -mt-2"
      onClick={onClose}
      disabled={closeDisabled}
      aria-label="Close"
    >
      <CloseRoundedIcon fontSize="small" />
    </button>
  </div>
);

/** Yes/no confirmation. `onConfirm` may be async; errors show in the dialog. */
export const ConfirmDialog = ({ open, title, children, confirmLabel = "Confirm", danger, onConfirm, onClose }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setBusy(false);
      setError(null);
    }
  }, [open]);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { m: 2 } }}>
      <div className="p-5">
        <h2 className="text-lg font-semibold leading-tight">{title}</h2>
        <div className="app-muted mt-2 space-y-2 text-sm">{children}</div>
        {error && (
          <div className="mt-3">
            <Notice tone="rose">{error}</Notice>
          </div>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="app-btn app-btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className={`app-btn ${danger ? "app-btn-danger" : "app-btn-primary"}`}
            onClick={confirm}
            disabled={busy}
          >
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

/** Read-only detail of one safety concern: full text, photos, notes. */
export const ConcernDialog = ({ concern, userId, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [photo, setPhoto] = useState(null);

  useEffect(() => {
    setDetail(null);
    setError(null);
    setPhoto(null);
    if (!concern) return undefined;
    let active = true;
    fetchSafetyConcern(userId, concern.id)
      .then((full) => active && setDetail(full))
      .catch((e) => active && setError(e.message));
    return () => {
      active = false;
    };
  }, [concern, userId]);

  const shown = detail || concern;

  return (
    <Dialog
      open={Boolean(concern)}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { m: 2 } }}
      aria-labelledby="safety-concern-title"
    >
      {shown && (
        <div className="p-5 sm:p-6">
          <DialogHeader
            id="safety-concern-title"
            icon={ReportProblemRoundedIcon}
            title="Safety concern"
            subtitle={`${shown.branch || "No branch"} · ${formatDateTime(shown.createdAt)}`}
            onClose={onClose}
          />

          <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
            <ConcernStatusChip status={shown.status} />
            <span className="app-muted">Reported by {shown.submitterName || "someone"}</span>
          </div>

          <p className="mt-3 whitespace-pre-line break-words text-sm leading-relaxed">
            {shown.description || "No description."}
          </p>

          {error && (
            <div className="mt-4">
              <Notice tone="rose">{error}</Notice>
            </div>
          )}
          {!detail && !error && (
            <div className="mt-4 flex items-center gap-2 text-sm">
              <Spinner size={18} /> <span className="app-muted">Loading photos and notes…</span>
            </div>
          )}

          {detail?.photos?.length > 0 && (
            <div className="mt-4">
              <p className="app-eyebrow mb-2">Photos</p>
              {photo ? (
                <button
                  type="button"
                  onClick={() => setPhoto(null)}
                  className="app-light-surface block w-full overflow-hidden rounded-xl"
                  aria-label="Back to all photos"
                >
                  <img src={photo.data} alt={photo.name || "Safety concern photo"} className="max-h-[60vh] w-full object-contain" />
                </button>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {detail.photos.map((p, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setPhoto(p)}
                      className="aspect-square overflow-hidden rounded-xl border app-divider"
                      aria-label={`View photo ${i + 1} larger`}
                    >
                      <img src={p.data} alt={p.name || `Photo ${i + 1}`} className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {detail && (
            <div className="mt-4">
              <p className="app-eyebrow mb-2">Notes</p>
              {detail.notes.length === 0 ? (
                <p className="app-muted text-sm">No notes yet.</p>
              ) : (
                <ul className="space-y-2">
                  {detail.notes.map((note, i) => (
                    <li key={i} className="app-surface rounded-xl px-3 py-2 text-sm">
                      <p className="whitespace-pre-line break-words">{note.text}</p>
                      <p className="app-muted mt-0.5 text-xs">
                        {note.authorName || "Safety team"} · {formatDateTime(note.timestamp)}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <p className="app-muted mt-5 border-t app-divider pt-3 text-xs">
            To change the status or add a note, use Safety Manager → Safety Submissions in the Big C
            mobile app.
          </p>
        </div>
      )}
    </Dialog>
  );
};

// ---------------------------------------------------------------- upload

const formatSize = (bytes) =>
  bytes >= 1024 * 1024 * 1024
    ? `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`
    : `${Math.max(1, Math.round(bytes / 1024 / 1024))} MB`;

const isMp4File = (file) =>
  ["video/mp4", "video/x-m4v"].includes(file.type) || /\.(mp4|m4v)$/i.test(file.name);

/**
 * Upload a toolbox talk or training video. `kind` ("toolbox" | "training")
 * opens the dialog; null closes it.
 */
export const UploadVideoDialog = ({ kind, userId, onClose, onUploaded }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null);
  const [done, setDone] = useState(null);
  const inputRef = useRef(null);

  const open = Boolean(kind);
  const uploading = progress !== null && !done;
  const meta = VIDEO_KINDS[kind] || VIDEO_KINDS.toolbox;

  // Fresh form every time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setTitle("");
    setDescription("");
    setFile(null);
    setError(null);
    setProgress(null);
    setDone(null);
  }, [open]);

  const pickFile = (picked) => {
    setError(null);
    if (!picked) return;
    if (!isMp4File(picked)) {
      setFile(null);
      setError("Choose an MP4 video. Other formats (like .mov) often won't play in a browser.");
      return;
    }
    if (picked.size > MAX_VIDEO_MB * 1024 * 1024) {
      setFile(null);
      setError(`That video is ${formatSize(picked.size)} — the limit is ${MAX_VIDEO_MB} MB.`);
      return;
    }
    setFile(picked);
    // Suggest a title from the file name if none was typed yet.
    if (!title.trim()) setTitle(picked.name.replace(/\.(mp4|m4v)$/i, "").replace(/[_-]+/g, " ").trim());
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file || !title.trim()) return;
    setError(null);
    setProgress(0);
    try {
      const saved = await uploadSafetyVideo(
        userId,
        kind,
        { title: title.trim(), description: description.trim(), file },
        setProgress
      );
      setDone(saved);
      onUploaded?.(saved);
    } catch (err) {
      setProgress(null);
      setError(err.message);
    }
  };

  const close = () => {
    if (!uploading) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { m: 2 } }}
      aria-labelledby="safety-upload-title"
    >
      <form className="p-5 sm:p-6" onSubmit={submit}>
        <DialogHeader
          id="safety-upload-title"
          icon={CloudUploadRoundedIcon}
          tone="sky"
          title={`Upload a ${meta.noun}`}
          subtitle={
            kind === "training"
              ? "Only the people you assign it to will see it."
              : "Everyone can watch it on the website and in the mobile app."
          }
          onClose={close}
          closeDisabled={uploading}
        />

        {done ? (
          <div className="mt-5">
            <p className="flex items-start gap-2 text-sm font-semibold">
              <CheckCircleRoundedIcon className="text-emerald-600 dark:text-emerald-400" fontSize="small" />
              “{done.title}” is uploaded.
            </p>
            <p className="app-muted mt-2 text-sm">
              {kind === "training"
                ? "Nobody sees it until it's assigned. To add a quiz, use Safety Manager → Training Videos in the mobile app."
                : "Everyone can watch it now in Toolbox Talks. To add a quiz, use Safety Manager → Toolbox Talks in the mobile app."}
            </p>
            <div className="mt-5 flex justify-end gap-2">
              {kind === "training" ? (
                <>
                  <button type="button" className="app-btn app-btn-ghost" onClick={onClose}>
                    Later
                  </button>
                  <Link to={`/safety/training/${done.id}?assign=1`} className="app-btn app-btn-primary" onClick={onClose}>
                    Assign it now
                  </Link>
                </>
              ) : (
                <button type="button" className="app-btn app-btn-primary" onClick={onClose}>
                  Done
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="mt-5 space-y-4">
            <div>
              <input
                ref={inputRef}
                type="file"
                accept="video/mp4,.mp4,.m4v"
                className="hidden"
                onChange={(e) => {
                  pickFile(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
              <button
                type="button"
                disabled={uploading}
                onClick={() => inputRef.current?.click()}
                className="app-surface flex w-full items-center gap-3 rounded-xl border border-dashed px-4 py-3 text-left text-sm transition hover:bg-[color:var(--app-hover)]"
              >
                <MovieRoundedIcon className="app-muted" />
                {file ? (
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-semibold">{file.name}</span>
                    <span className="app-muted text-xs">{formatSize(file.size)} · click to choose a different video</span>
                  </span>
                ) : (
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold">Choose a video</span>
                    <span className="app-muted text-xs">MP4, up to {MAX_VIDEO_MB} MB</span>
                  </span>
                )}
              </button>
            </div>

            <TextField
              fullWidth
              size="small"
              label="Title"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={uploading}
              inputProps={{ maxLength: 200 }}
              placeholder={kind === "training" ? "e.g. Load securement basics" : "e.g. October 2026 Forklift Safety"}
            />
            <TextField
              fullWidth
              size="small"
              label="Description (optional)"
              multiline
              minRows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={uploading}
              inputProps={{ maxLength: 2000 }}
            />

            {error && <Notice tone="rose">{error}</Notice>}

            {uploading && (
              <div>
                <LinearProgress
                  variant={progress >= 1 ? "indeterminate" : "determinate"}
                  value={Math.round(progress * 100)}
                />
                <p className="app-muted mt-1.5 text-xs">
                  {progress >= 1
                    ? "Saving…"
                    : `Uploading ${Math.round(progress * 100)}% — keep this page open until it finishes.`}
                </p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <button type="button" className="app-btn app-btn-ghost" onClick={close} disabled={uploading}>
                Cancel
              </button>
              <button
                type="submit"
                className="app-btn app-btn-primary"
                disabled={!file || !title.trim() || uploading}
              >
                <CloudUploadRoundedIcon sx={{ fontSize: 18 }} />
                Upload
              </button>
            </div>
          </div>
        )}
      </form>
    </Dialog>
  );
};
